import { useMemo } from "react";

import { CloseIcon, SearchIcon } from "@/assets/icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { capitalizeFirst } from "@/helpers/string";
import { cn } from "@/lib/utils";
import { DatasetDataType } from "@/types/dataset";

import Divider from "./Divider";

type FilterOption = {
  label: string;
  value: string;
  type: DatasetDataType;
};

export type SearchFilterParams = {
  query: string | null;
  query_type?: DatasetDataType | null;
  query_column?: string | null;
};

interface SearchWithSelectProps<T extends SearchFilterParams> {
  searchPlaceholder?: string;
  optionPlaceholder?: string;
  searchParams: T;
  options: FilterOption[];
  onParamsChange: (params: SearchFilterParams) => void;
  disabled?: boolean;
  className?: string;
}

const SearchWithSelect = <T extends SearchFilterParams>({
  searchPlaceholder = "Search by keyword",
  optionPlaceholder = "Loading options",
  searchParams,
  options,
  disabled = true,
  onParamsChange,
  className
}: SearchWithSelectProps<T>) => {
  const memoizedFilterOptions = useMemo(() => options, [options]);

  const handleTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onParamsChange?.({
      ...searchParams,
      query: value || null
    } as T);
  };

  const clearSearch = () => {
    onParamsChange?.({
      ...searchParams,
      query: null
    });
  };

  const handleFilterChange = (item: FilterOption) => {
    onParamsChange?.({
      ...searchParams,
      query_type: item.type,
      query_column: item.value
    });
  };

  const onFilterValueChange = (selectedValue: string) => {
    const item = memoizedFilterOptions.find(
      (opt) => opt.value === selectedValue
    );
    if (item) {
      handleFilterChange(item);
    }
  };

  return (
    <div
      className={cn(
        "flex items-center gap-3 text-sm justify-between",
        className
      )}
    >
      <div
        className={cn(
          "flex basis-[560px] items-center gap-1 border-[1px] border-neutral-200 rounded-[12px] pl-3 pr-1 h-[40px]",
          disabled ? "bg-neutral-50" : ""
        )}
      >
        <div className="flex basis-[560px] items-center gap-1 border-none rounded-[12px] h-[40px]">
          <SearchIcon className="fill-neutral-300 w-5" />
          <input
            className="focus:outline-none w-full text-sm text-neutral-900 disabled:!bg-neutral-50 disabled:text-neutral-500"
            placeholder={searchPlaceholder}
            value={searchParams.query || ""}
            disabled={disabled}
            onChange={handleTermChange}
            aria-label="Search input"
          />
        </div>

        {searchParams.query && (
          <button
            className="text-neutral-500 hover:text-neutral-900"
            onClick={clearSearch}
            aria-label="Clear search"
          >
            <CloseIcon className="fill-neutral-600 h-5" />
          </button>
        )}
        <Divider type="vertical" className="h-[22px] ml-2" />
        <Select
          value={searchParams.query_column || memoizedFilterOptions[0]?.value}
          onValueChange={onFilterValueChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={cn(
              "max-w-[200px] px-2 text-sm flex items-center gap-1 text-neutral-600 disabled:text-neutral-500 disabled:cursor-not-allowed border-none shadow-none focus:ring-0 h-full",
              disabled ? "bg-neutral-50" : "bg-white"
            )}
            aria-label="Filter column"
          >
            <SelectValue
              placeholder={memoizedFilterOptions[0]?.label || optionPlaceholder}
            />
          </SelectTrigger>
          <SelectContent
            showScrollButtons={false}
            className="z-[1100] rounded-xl"
            side="bottom"
          >
            {memoizedFilterOptions.map((item, index) => (
              <SelectItem
                key={`${item.value}-${index}`}
                className="text-left px-3 cursor-pointer"
                value={item.value}
              >
                {capitalizeFirst(item.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default SearchWithSelect;
